import React, { useState, useEffect } from 'react';
import { useForm } from "react-hook-form";
import { Link, useParams, useHistory } from "react-router-dom";
import clean from '../../utils/Clean';
import only from '../../utils/Only';
import Spinner from '../../components/Spinner';
import GeneralSettingsSkeleton from '../../components/skeletons/pages/GeneralSettingsSkeleton';
import * as Sentry from '@sentry/react';
import APIKeysSection from '../installation/APIKeysSection';
import { Modal } from 'react-bootstrap';
import { useIntegration, useUpdateIntegrationGeneral, useDeleteIntegration } from '../../http-hooks/integration/useIntegration';
import { Card, PageHeader } from '../../components/ui/bootstrap';
const classNames = require('classnames');

function General() {

    const history = useHistory();
    const { org_id, id } = useParams();
    const [showConfirmation, setShowConfirmation] = useState(false);

    // ✨ Migrated to React Query
    const { data: integration, isLoading } = useIntegration({ org_id, integration_id: id });
    const { mutateAsync: updateGeneral, isPending: isUpdating } = useUpdateIntegrationGeneral({ org_id, integration_id: id });
    const { mutateAsync: deleteProject, isPending: isDeletingProject } = useDeleteIntegration({ org_id });

    const { register, reset, formState: { errors }, handleSubmit } = useForm();

    const onSubmit = async (data) => {
        data = clean(data);

        try {
            await updateGeneral(data);
        } catch (error) {
            Sentry.captureException(error);
        }
    };

    useEffect(() => {
        if (integration) {
            reset(only(integration, ["integration_name", "description", "site_url", "category", "platform", "lang_code"]));
        }
    }, [integration, reset]);

    const handleDeleteProject = async () => {
        try {
            await deleteProject(id);
            setShowConfirmation(false);
            history.push(`/organisation/${org_id}/integration`);
        } catch (error) {
            Sentry.captureException(error);
        }
    }


    return (
        <React.Fragment>
            <Modal show={showConfirmation} onHide={() => { setShowConfirmation(false) }}>
                <Modal.Header closeButton>
                    <Modal.Title>Delete Project</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Are you sure you want to delete this project?</p>
                </Modal.Body>
                <Modal.Footer>
                    <button type="button" className="btn btn-outline-primary me-3" onClick={() => setShowConfirmation(false)}>Cancel</button>
                    {
                        (isDeletingProject) ?
                            (
                                <button type="button" className="btn btn-danger text-center">
                                    <Spinner color="white" />
                                </button>
                            )
                            :
                            (
                                <button type="button" className="btn btn-danger" onClick={() => handleDeleteProject()}>Delete</button>
                            )
                    }
                </Modal.Footer>
            </Modal>
            <PageHeader title="Project Settings" subtitle="General configuration settings for your project" />
            {
                (isLoading)
                    ?
                    (
                        <GeneralSettingsSkeleton />
                    )
                    :
                    (
                        <div className="row">
                            <div className="col-xl-6">
                                <form onSubmit={handleSubmit(onSubmit)}>
                                    <div className="d-flex flex-column gap-4">
                                        <Card className="p-4" shadow>
                                            <p className="fw-semibold mb-3 text-body-emphasis" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>General Settings</p>
                                            <div className="mb-3">
                                                <label className="form-label fw-medium" style={{ fontSize: '0.875rem' }}>Name</label>
                                                <input type="text" className={classNames("form-control", { "is-invalid": errors.integration_name })} {...register("integration_name", { required: true, minLength: 3 })} />
                                                {
                                                    errors.integration_name &&
                                                    <div className="invalid-feedback">
                                                        {
                                                            ("required" === errors.integration_name.type) && "Name field is required."
                                                        }
                                                        {
                                                            ("minLength" === errors.integration_name.type) && "Name field should be at least 3 character long."
                                                        }
                                                    </div>
                                                }
                                            </div>
                                        </Card>
                                        <APIKeysSection orgId={org_id} integration={integration} />
                                    </div>
                                    <div className="d-flex mt-4 justify-content-end mx-0">
                                        <Link to={`/organisation/${org_id}/integration/${id}/content`}><button className="btn btn-outline-primary me-3">Cancel</button></Link>
                                        {
                                            (isUpdating) ?
                                                (
                                                    <button className="btn btn-primary" style={{ width: '145px' }} type="button">
                                                        <Spinner color="white" />
                                                    </button>
                                                )
                                                :
                                                (
                                                    <button className="btn btn-primary" style={{ width: '145px' }} type="submit">Save Changes</button>
                                                )
                                        }
                                    </div>
                                </form>
                                <div className="col">
                                    <div className="delete-project-separator"></div>
                                    <div className="mt-3 mb-3 d-flex delete-project-container">
                                        <div>
                                            <h5>Delete Project</h5>
                                            <p>Permanently delete this project including all content, links and settings</p>
                                        </div>
                                        <div>
                                            <button onClick={() => { setShowConfirmation(true) }} className="btn btn-danger delete-project-button">Delete Project</button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )
            }
        </React.Fragment>
    );
}

export default General;
